import { usesNativeRefreshBroker } from './nativeRefreshBroker';

export type NativeBridgeRefreshExport = {
    access_token?: string;
    refresh_token?: string;
    fp?: string;
    src?: string;
};

export const NATIVE_SESSION_MODE_HEADER = "X-Native-Session-Mode";
export const NATIVE_SESSION_MODE_HOST_ONLY_V1 = "host-only-v1";
export const BROWSER_SESSION_PROTOCOL_COOKIE_ONLY_V1 = "cookie-only-v1";

export type NativeCapabilities = {
    protocolVersion?: number;
    supportsNativeLoginRedirect?: boolean;
    supportsSessionBundle?: boolean;
    nativeSessionMode?: typeof NATIVE_SESSION_MODE_HOST_ONLY_V1;
};

const REFRESH_TOKEN_STORAGE_KEY = "refresh_token";
const FP_STORAGE_KEY = "csrf_fp";

function readSessionStorageValue(key: string): string {
    try {
        if (typeof window === "undefined" || !window.sessionStorage) {
            return "";
        }

        return window.sessionStorage.getItem(key) || "";
    } catch {
        return "";
    }
}

function writeSessionStorageValue(key: string, value: string): void {
    try {
        if (typeof window === "undefined" || !window.sessionStorage) {
            return;
        }

        if (value) {
            window.sessionStorage.setItem(key, value);
        } else {
            window.sessionStorage.removeItem(key);
        }
    } catch {
        // ignore
    }
}

function readLocalStorageValue(key: string): string {
    try {
        if (typeof window === "undefined" || !window.localStorage) {
            return "";
        }

        return window.localStorage.getItem(key) || "";
    } catch {
        return "";
    }
}

function getCookie(name: string): string {
    try {
        if (typeof document === "undefined") return "";
        const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const match = document.cookie.match(new RegExp("(?:^|;\\s*)" + escaped + "=([^;]+)"));
        return match ? decodeURIComponent(match[1]) : "";
    } catch {
        return "";
    }
}

function normalizeToken(token: string | null | undefined): string {
    return String(token || "").trim().replace(/^['"]+|['"]+$/g, "");
}

export function isInWebView(): boolean {
    try {
        if (typeof window === "undefined") return false;
        const w = window as any;
        if (w.ReactNativeWebView || w.__CC_NATIVE_CAPABILITIES__ || w.__CC_NATIVE_SESSION_CONTEXT__) {
            return true;
        }

        const ua = String(window.navigator?.userAgent || "").toLowerCase();
        return ua.includes("; wv") || /\bwv\b/.test(ua);
    } catch {
        return false;
    }
}

export function getNativeCapabilities(): NativeCapabilities {
    try {
        const raw = (window as any).__CC_NATIVE_CAPABILITIES__;
        return raw && typeof raw === "object" ? raw as NativeCapabilities : {};
    } catch {
        return {};
    }
}

export function getNativeSessionModeHeaders(): Record<string, string> {
    return getNativeCapabilities().nativeSessionMode === NATIVE_SESSION_MODE_HOST_ONLY_V1
        ? { [NATIVE_SESSION_MODE_HEADER]: NATIVE_SESSION_MODE_HOST_ONLY_V1 }
        : {};
}

export function getBrowserSessionProtocolPayload(): Record<string, string> {
    return isInWebView()
        ? {}
        : { session_protocol: BROWSER_SESSION_PROTOCOL_COOKIE_ONLY_V1 };
}

function getNativeSessionEpoch(): number | undefined {
    try {
        const raw = (window as any).__CC_NATIVE_SESSION_CONTEXT__?.sessionEpoch;
        if (typeof raw !== "number" || !Number.isFinite(raw) || raw < 0) {
            return undefined;
        }

        return Math.floor(raw);
    } catch {
        return undefined;
    }
}

export function getFingerprint(): string {
    return getCookie("fp") || readLocalStorageValue(FP_STORAGE_KEY);
}

export function getStoredRefreshToken(): string {
    if (usesNativeRefreshBroker()) { writeSessionStorageValue(REFRESH_TOKEN_STORAGE_KEY, ''); return ''; }
    if (!isInWebView()) {
        // Browser sessions rotate exclusively through HttpOnly cookies. Never
        // replay a legacy readable token after the server has rotated it.
        writeSessionStorageValue(REFRESH_TOKEN_STORAGE_KEY, "");
        return "";
    }

    const storedToken = normalizeToken(readSessionStorageValue(REFRESH_TOKEN_STORAGE_KEY));
    if (storedToken) {
        return storedToken;
    }

    const cookieToken = normalizeToken(getCookie(REFRESH_TOKEN_STORAGE_KEY));
    if (cookieToken) {
        persistRefreshToken(cookieToken);
        return cookieToken;
    }

    return storedToken;
}

export function persistRefreshToken(token: string): void {
    if (usesNativeRefreshBroker()) { writeSessionStorageValue(REFRESH_TOKEN_STORAGE_KEY, ''); return; }
    writeSessionStorageValue(
        REFRESH_TOKEN_STORAGE_KEY,
        isInWebView() ? normalizeToken(token) : ""
    );
}

export function exportSessionToNative(data: NativeBridgeRefreshExport): boolean {
    try {
        if (usesNativeRefreshBroker()) return false;
        if (!isInWebView()) return false;
        if (!data.access_token && !data.refresh_token) return false;

        const bridge = (window as any).ReactNativeWebView;
        if (!bridge || typeof bridge.postMessage !== "function") {
            return false;
        }

        const epoch = getNativeSessionEpoch();
        bridge.postMessage(JSON.stringify({
            v: 1,
            type: "refresh_export_inline",
            data,
            ...(epoch == null ? {} : { epoch }),
        }));
        return true;
    } catch {
        return false;
    }
}

export function notifyNativeSessionInvalid(reason: string): boolean {
    // Missing cookies during bootstrap and network errors do not prove that
    // the native session is invalid. Only report a definitive server refusal.
    if (!['token_reused', 'invalid_or_expired', 'fingerprint_mismatch'].includes(reason)) {
        return false;
    }
    try {
        const bridge = (window as any).ReactNativeWebView;
        if (!isInWebView() || !bridge || typeof bridge.postMessage !== 'function') {
            return false;
        }
        const epoch = getNativeSessionEpoch();
        bridge.postMessage(JSON.stringify({
            v: 1,
            type: 'session_invalid',
            data: {reason},
            ...(epoch == null ? {} : {epoch}),
        }));
        return true;
    } catch {
        return false;
    }
}
